import type { Writable } from 'type-fest';

import type { ReportableError } from '@change-corgi/core/errorReporter/common';
import type { GqlFetch } from '@change-corgi/core/gql';

import { arrayOfAll } from '#src/app/shared/utils/arrays';

import {
	FeCoreSession,
	type FeCoreSessionQuery,
	type FeCoreSessionQueryVariables,
	type FeSessionUserFragment,
} from './session.graphql';
import type { SessionExperimentsFragment } from './sessionExperimentsFragment.graphql';
import type { SessionFeatureConfigsFragment } from './sessionFeatureConfigsFragment.graphql';

type SessionInitial = FeCoreSessionQuery['session'];
export type SessionUser = FeSessionUserFragment & {
	/**
	 * if undefined, value couldn't be retrieved due to permissions
	 *
	 * if null, value is not set in the DB
	 */
	createdAt?: string | null;
};
export type SessionUserRole = keyof Omit<
	SessionUser['roles'],
	'id' | 'countryTeam' | 'countryTeamAdmin' | '__typename'
>;
/** @knip-ignore */
export const SESSION_USER_ROLES = arrayOfAll<SessionUserRole>()([
	'audit',
	'auditAdmin',
	'campaigns',
	'communications',
	'data',
	'engineering',
	'petitionAdmin',
	'policy',
	'policyAdmin',
	'policySenior',
	'product',
	'staff',
	'staffAdmin',
	'support',
	'supportAdmin',
	'supportSenior',
	'userResearch',
]);
/** @knip-ignore */
export type SessionUserScopedRole = keyof Pick<SessionUser['roles'], 'countryTeam' | 'countryTeamAdmin'>;
/** @knip-ignore */
export const SESSION_USER_SCOPED_ROLES = arrayOfAll<SessionUserScopedRole>()(['countryTeam', 'countryTeamAdmin']);
/** @knip-ignore */
export type SessionUserScopedRoleDetails = SessionUser['roles']['countryTeam'][0];

type BaseSession = Omit<SessionInitial, 'user' | 'userAuthOnly' | 'loginState' | 'id'>;

export type GuestSession = BaseSession &
	Readonly<{
		loginState: Extract<SessionInitial['loginState'], 'GUEST'>;
		user: null;
	}>;
// TODO possible improvement: use discriminant to only add createdAt for loginState==AUTHENTICATED
/** @knip-ignore */
export type AuthSession = BaseSession &
	Readonly<{
		loginState: Exclude<SessionInitial['loginState'], 'GUEST'>;
		user: SessionUser;
	}>;

export type Session = GuestSession | AuthSession;

export type SessionConfiguration = {
	/**
	 * this makes sure that the 'guest' status of the user will be available even when
	 * access to the session is not possible (e.g. when using server-cache rendering)
	 */
	initialGuestState: boolean;
	/**
	 * whether the user is a petition starter (i.e. has the petition-admin role)
	 *
	 * derived from the session and exposed here so it's part of the SSR server-cache key,
	 * allowing starter-gated rendering decisions to survive server-cache rendering
	 */
	userIsStarter: boolean;
	featureConfigs: SessionFeatureConfigsFragment;
	experiments: Omit<SessionExperimentsFragment, '__typename'>;
};

type Options = Readonly<{
	gqlFetch: GqlFetch;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	reportError: (error: ReportableError) => Promise<any>;
}>;

function buildSessionAndConfiguration(
	session: Session,
	featureConfigs: SessionFeatureConfigsFragment | undefined,
	experiments: SessionExperimentsFragment | undefined,
): { session: Session; sessionConfiguration: SessionConfiguration } {
	if (!featureConfigs || !experiments) {
		throw new Error('Feature configs or experiments are missing in the response');
	}
	// eslint-disable-next-line no-param-reassign
	delete (experiments as Partial<Writable<SessionExperimentsFragment>>).__typename;
	return {
		session,
		sessionConfiguration: {
			featureConfigs,
			experiments,
			initialGuestState: session.loginState === 'GUEST',
			userIsStarter: !!session.user?.roles.petitionAdmin,
		},
	};
}

function getSessionInner(
	{ gqlFetch, reportError }: Options,
	includeConfig: true,
): Promise<{ session: Session; sessionConfiguration: SessionConfiguration }>;
function getSessionInner(
	{ gqlFetch, reportError }: Options,
	includeConfig: false,
): Promise<{ session: Session; sessionConfiguration: undefined }>;
async function getSessionInner(
	{ gqlFetch, reportError }: Options,
	includeConfig: boolean,
): Promise<{ session: Session; sessionConfiguration?: SessionConfiguration }> {
	const response = await gqlFetch<FeCoreSessionQuery, FeCoreSessionQueryVariables>({
		batched: false,
		query: FeCoreSession,
		variables: {
			includeConfig,
		},
		rejectOnError: true,
	});
	const sessionWithId = response.data.session;
	const { id, ...session } = sessionWithId;
	const { featureConfigs, experiments } = response.data;
	if (session.loginState !== 'GUEST' && !session.user) {
		// should not happen => let's report it
		const invalidSession = session;
		void reportError({
			error: `user info missing in Viewer for ${invalidSession.loginState} user`,
			params: {
				loginState: invalidSession.loginState,
				uuid: invalidSession.uuid,
			},
		});
		// assume the session to be a guest session to avoid issues
		const sessionFallback = {
			...invalidSession,
			loginState: 'GUEST' as const,
		} as GuestSession;
		return includeConfig
			? buildSessionAndConfiguration(sessionFallback, featureConfigs, experiments)
			: { session: sessionFallback };
	}
	if (session.user && session.userAuthOnly) {
		session.user = {
			...session.user,
			...session.userAuthOnly,
		};
	}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
	delete (session as any).userAuthOnly;
	return includeConfig
		? buildSessionAndConfiguration(session as Session, featureConfigs, experiments)
		: { session: session as Session };
}

export async function getSessionWithConfig(
	options: Options,
): Promise<{ session: Session; sessionConfiguration: SessionConfiguration }> {
	return getSessionInner(options, true);
}

export async function getSession(options: Options): Promise<Session> {
	return (await getSessionInner(options, false)).session;
}
